import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Image, Text, useDisclosure, Collapse } from '@chakra-ui/react';
import I18n from '../I18n';
import DownArrow from '../svgIcon/downArrow';
import CustomLink from '../customLink';
import gtagEvent from '../../utils/gtag';

/**
 * Display an open drop down menu with the menu title
 * (This is for the list in the non-desktop mode of the header)
 * The menu consists of a list of links
 */
const ToggleDropDownMenu = ({
  items,
  onClickCallback,
  children,
  titleStyles,
  listItemStyles,
  noMargin,
  disabled,
  noArrow,
  ...rest
}) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Box position="relative" {...rest}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        cursor={disabled ? 'auto' : 'pointer'}
        px="space.m"
        py="space.s"
        sx={{
          WebkitTapHighlightColor: 'transparent',
        }}
        onClick={() => {
          gtagEvent('bloctoweb_click_product', {
            location: 'header',
          });
          onToggle();
        }}
      >
        <Text>{children}</Text>
        {!noArrow && <DownArrow />}
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex flexDirection="column" alignItems="flex-start">
          {items.map((item) => (
            <CustomLink
              key={item.titleTranslationKey}
              to={item?.href}
              onClick={() => {
                onClickCallback();
                item.onClick?.();
              }}
              width="100%"
              padding="space.s"
              my="space.3xs"
              _first={{ mt: 'space.2xs' }}
              _last={{ mb: 'space.4xs' }}
              _hover={{
                borderRadius: '100px',
                background: 'rgba(249, 249, 249, 0.60)',
                boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
                color: 'font.highlight',
              }}
              isExternal={item.isExternal}
              gtag={item.gtag}
            >
              {item?.icon ? <Image w="33px" h="33px" src={item.icon} /> : <Box mx="10px" />}
              <Box ml="space.4xs" textAlign="left">
                <Text fontSize="16px" lineHeight="130%">
                  {item.titleTranslationKey ? <I18n id={item.titleTranslationKey} /> : item.title}
                </Text>
                {!!item?.descTranslationKey && (
                  <Text fontSize="size.help.1" color="font.secondary" lineHeight="18px">
                    <I18n id={item.descTranslationKey} />
                  </Text>
                )}
              </Box>
            </CustomLink>
          ))}
        </Flex>
      </Collapse>
    </Box>
  );
};

ToggleDropDownMenu.propTypes = {
  children: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      titleTranslationKey: PropTypes.string,
    })
  ).isRequired,
  onClickCallback: PropTypes.func,
  defaultIsOpen: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  titleStyles: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  listItemStyles: PropTypes.object,
  isArrowIconShown: PropTypes.bool,
  noMargin: PropTypes.bool,
  disabled: PropTypes.bool,
  noArrow: PropTypes.bool,
};

ToggleDropDownMenu.defaultProps = {
  children: null,
  onClickCallback: null,
  defaultIsOpen: false,
  titleStyles: undefined,
  listItemStyles: undefined,
  isArrowIconShown: true,
  noMargin: false,
  disabled: false,
  noArrow: false,
};

export default ToggleDropDownMenu;
